import { useLayoutEffect, useState } from 'react';

/**
 * Keeps a panel that opens under a button ON the screen.
 *
 * Two of these went off the edge in different places — the morning-summary
 * clock in Settings, and the labels menu in the week — and the reason is the
 * same one both times. A panel positioned `absolute` lives inside whatever
 * ancestors it happens to have: if one of them scrolls or clips, the panel is
 * measured against the wrong box, and nudging it "left by however much it
 * overhangs the window" is arithmetic on a coordinate the panel does not use.
 * Fixing each site by hand means fixing it again at the next site.
 *
 * `position: fixed` takes the panel out of that argument entirely: its
 * coordinates ARE the viewport's, so clamping to the viewport is exact and no
 * ancestor can clip it. The anchor's real position is read from the DOM rather
 * than assumed, so it also survives a language that makes the button wider.
 *
 * Returns the style to spread onto the panel, or null while it is closed.
 */
const MARGIN = 8;   // never nearer the edge than this
const GAP = 6;      // between the button and the panel

export function useAnchoredPanel(open, anchorRef, panelRef, opts = {}) {
  const { align = 'end' } = opts;   // which edge of the panel meets the anchor's
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return undefined; }

    function place() {
      const a = anchorRef.current;
      const p = panelRef.current;
      if (!a) return;
      const ar = a.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Before the first measurement the panel has no width yet; assume it
      // wants the whole usable width, which is also the widest it may become.
      const pw = p ? p.getBoundingClientRect().width : (vw - MARGIN * 2);
      const ph = p ? p.getBoundingClientRect().height : 0;

      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const startEdge = rtl ? (vw - ar.right) : ar.left;
      const endEdge = rtl ? (vw - ar.left) : ar.right;

      // Line the chosen edge up with the button, then pull it back inside.
      let left = align === 'start' ? startEdge : endEdge - pw;
      if (left + pw > vw - MARGIN) left = vw - MARGIN - pw;
      if (left < MARGIN) left = MARGIN;

      // Below the button, unless there is more room above it.
      let top = ar.bottom + GAP;
      if (ph && top + ph > vh - MARGIN && ar.top - GAP - ph > MARGIN) {
        top = ar.top - GAP - ph;
      }

      setPos({
        position: 'fixed',
        top: Math.round(top),
        left: Math.round(left),
        maxWidth: vw - MARGIN * 2,
        maxHeight: Math.max(140, vh - top - MARGIN),
      });
    }

    // Twice: once to give the panel a place to be measured in, and once with
    // its real size known. A single pass would position a panel of width zero.
    place();
    const raf = requestAnimationFrame(place);

    window.addEventListener('resize', place);
    // `true` so a scroll inside ANY container moves the panel with its button,
    // instead of leaving it hanging where the button used to be.
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, panelRef, align]);

  return pos;
}

export default useAnchoredPanel;
